import {
  Timelapse,
  Timer,
  TimerOff,
  RunningWithErrors,
  Block,
} from '@mui/icons-material';
import { Chip, Box, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { OrderStatus, ProcessStep } from '../common/enums/enums';
import { SkeletonLoadingConfig } from '../global/globalSettings';
import { differenceDateTime } from '../helpers/date';
import {
  OrderStatusTag,
  WOStatusChips,
} from './WorkOrderForVehicle/StatusChips';
import { calculateLatestOrder } from './WorkOrderForVehicle/StatusChips/helper';

const PREFIX = 'StatusButton';

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  root: `${PREFIX}-root`,
  onTime: `${PREFIX}-onTime`,
  dueSoon: `${PREFIX}-dueSoon`,
  late: `${PREFIX}-late`,
  paused: `${PREFIX}-paused`,
  notApplicable: `${PREFIX}-notApplicable`,
  icons: `${PREFIX}-icons`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.mainContainer}`]: {
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
  },

  [`& .${classes.root}`]: {
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(0),
    },
  },

  [`& .${classes.onTime}`]: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    height: ({ isMobile }) => (isMobile ? '24px' : '32px'),
  },

  [`& .${classes.dueSoon}`]: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
    height: ({ isMobile }) => (isMobile ? '24px' : '32px'),
  },

  [`& .${classes.paused}`]: {
    backgroundColor: theme.palette.grey[700],
    color: theme.palette.common.white,
    height: ({ isMobile }) => (isMobile ? '24px' : '32px'),
  },

  [`& .${classes.notApplicable}`]: {
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
  },

  [`& .${classes.late}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    height: ({ isMobile }) => (isMobile ? '24px' : '32px'),
  },
}));

const iconStyles = {
  color: 'inherit',
  width: '18px',
  height: '18px',
  marginLeft: '4px',
  marginRight: '-4px',
};
const OnTime = ({ t }) => (
  <Chip
    label={t('card.statusButton.ontime')}
    className={classes.onTime}
    sx={{
      '& .MuiChip-icon': {
        ...iconStyles,
      },
    }}
    icon={<Timer />}
  />
);

const Late = ({ t }) => (
  <Chip
    label={t('card.statusButton.late')}
    className={classes.late}
    sx={{
      '& .MuiChip-icon': {
        ...iconStyles,
      },
    }}
    icon={<TimerOff />}
  />
);

const getReconStatusChip = (t, status, orderCreated, sla) => {
  const {
    ARRIVED,
    IN_PROCESS,
    DONE,
    CANCELED,
    DECLINED,
    PAUSED,
    MECHANICAL,
    WHOLE_SALE,
  } = ProcessStep;
  const { dueSoon, paused, notApplicable } = classes;
  const differenceDateInMinutes = differenceDateTime(
    orderCreated,
    'minutes'
  ).minutes;
  switch (status) {
    case PAUSED:
      return (
        <Chip
          label={t('card.statusButton.paused')}
          className={paused}
          sx={{
            '& .MuiChip-icon': {
              ...iconStyles,
            },
          }}
          icon={<RunningWithErrors />}
        />
      );
    case CANCELED:
    case DECLINED:
      return (
        <Chip
          label={t('card.statusButton.notApplicable')}
          className={notApplicable}
          sx={{
            '& .MuiChip-icon': {
              ...iconStyles,
            },
          }}
          icon={<Block />}
        />
      );
    case DONE:
      if (differenceDateInMinutes <= sla) {
        return <OnTime t={t} />;
      }
      return <Late t={t} />;

    case ARRIVED:
    case IN_PROCESS:
      if (differenceDateInMinutes < sla * 0.8) return <OnTime t={t} />;
      if (
        differenceDateInMinutes > sla * 0.8 &&
        differenceDateInMinutes < sla * 0.99
      )
        return (
          <Chip
            label={t('card.statusButton.duesoon')}
            className={dueSoon}
            sx={{
              '& .MuiChip-icon': {
                ...iconStyles,
              },
            }}
            icon={<Timelapse />}
          />
        );
      if (differenceDateInMinutes > sla * 0.99) return <Late t={t} />;
      return <Late t={t} />;
    case MECHANICAL:
      return <OrderStatusTag status={WOStatusChips.MECHANICAL} />;
    case WHOLE_SALE:
      return <OrderStatusTag status={WOStatusChips.WHOLE_SALE} />;
    default:
      return <Late t={t} />;
  }
};

const getOrderStatusChip = (t, status) => {
  const { ON_TIME, DUE_SOON, PAUSED, NOT_APPLICABLE } = OrderStatus;
  const { onTime, dueSoon, paused, late, notApplicable } = classes;
  switch (status) {
    case ON_TIME:
      return (
        <Chip
          label={t('card.statusButton.ontime')}
          className={onTime}
          sx={{
            '& .MuiChip-icon': {
              ...iconStyles,
            },
          }}
          icon={<Timer />}
        />
      );
    case DUE_SOON:
      return (
        <Chip
          label={t('card.statusButton.duesoon')}
          className={dueSoon}
          sx={{
            '& .MuiChip-icon': {
              ...iconStyles,
            },
          }}
          icon={<Timelapse />}
        />
      );
    case PAUSED:
      return (
        <Chip
          label={t('card.statusButton.paused')}
          className={paused}
          sx={{
            '& .MuiChip-icon': {
              ...iconStyles,
            },
          }}
          icon={<RunningWithErrors />}
        />
      );
    case NOT_APPLICABLE:
      return (
        <Chip
          label={t('card.statusButton.notApplicable')}
          className={notApplicable}
          sx={{
            '& .MuiChip-icon': {
              ...iconStyles,
            },
          }}
          icon={<Block />}
        />
      );
    default:
      return (
        <Chip
          label={t('card.statusButton.late')}
          className={late}
          sx={{
            '& .MuiChip-icon': {
              ...iconStyles,
            },
          }}
          icon={<TimerOff />}
        />
      );
  }
};

const StatusButton = ({
  status,
  title = false,
  isLoading = false,
  order = {},
  sla,
  isReconditioning = false,
}) => {
  const { t } = useTranslation('workOrdersTable');
  const latestOrderDate = calculateLatestOrder(order);
  const orderCreated = new Date(latestOrderDate);
  return !isLoading ? (
    <StyledGrid container className={classes.mainContainer}>
      {title ? (
        <Grid item>
          <Box>
            <Typography variant="subtitle2">
              {t('detail.statusRow.statusLabel')}
            </Typography>
          </Box>
        </Grid>
      ) : null}
      <Grid item>
        <Box className={classes.root}>
          {isReconditioning
            ? getReconStatusChip(t, status, orderCreated, sla)
            : getOrderStatusChip(t, status)}
        </Box>
      </Grid>
    </StyledGrid>
  ) : (
    <StyledGrid container className={classes.mainContainer}>
      <Grid item>
        <Box>
          <Skeleton width={SkeletonLoadingConfig.width} />
        </Box>
      </Grid>
      <Grid item>
        <Box className={classes.root}>
          <Skeleton width={SkeletonLoadingConfig.width} />
        </Box>
      </Grid>
    </StyledGrid>
  );
};

export default StatusButton;
